/**
 * Workflow Amplitude events. Call sites pass ids and these enums; the builders are the only
 * place a payload is assembled, so a prompt, a file name, or a coin amount has nowhere to land.
 */
export const WORKFLOW_EVENT = {
  created: 'Workflows - Created',
  templateUsed: 'Workflows - Template Used',
  editorOpened: 'Workflows - Editor Opened',
  runStarted: 'Workflows - Run Started',
  runCompleted: 'Workflows - Run Completed',
  runFailed: 'Workflows - Run Failed',
  published: 'Workflows - Published',
  scheduled: 'Workflows - Scheduled',
} as const

export type WorkflowEventName = (typeof WORKFLOW_EVENT)[keyof typeof WORKFLOW_EVENT]

export type WorkflowCreateSource = 'blank' | 'describe' | 'template' | 'save_as_new'

export type WorkflowTemplateDestination = 'editor' | 'run_form'

export type WorkflowRunSurface = 'editor' | 'hub' | 'template_form'

export type WorkflowExecutionModeName = 'automatic' | 'manual_step'

export interface WorkflowCreatedProps extends Record<string, unknown> {
  workflow_id: string
  source: WorkflowCreateSource
}

export interface WorkflowTemplateUsedProps extends Record<string, unknown> {
  template_id: string
  destination: WorkflowTemplateDestination
}

export interface WorkflowEditorOpenedProps extends Record<string, unknown> {
  workflow_id: string
}

export interface WorkflowRunStartedProps extends Record<string, unknown> {
  workflow_id: string
  run_id: string
  execution_mode: WorkflowExecutionModeName
  surface: WorkflowRunSurface
}

export interface WorkflowRunFinishedProps extends Record<string, unknown> {
  workflow_id: string
  run_id: string
}

export interface WorkflowPublishedProps extends Record<string, unknown> {
  workflow_id: string
}

export interface WorkflowScheduledProps extends Record<string, unknown> {
  workflow_id: string
  schedule_id: string
}

export function workflowCreatedProps(
  workflowId: string,
  source: WorkflowCreateSource,
): WorkflowCreatedProps {
  return { workflow_id: workflowId, source }
}

export function workflowTemplateUsedProps(
  templateId: string,
  destination: WorkflowTemplateDestination,
): WorkflowTemplateUsedProps {
  return { template_id: templateId, destination }
}

export function workflowEditorOpenedProps(workflowId: string): WorkflowEditorOpenedProps {
  return { workflow_id: workflowId }
}

export function workflowRunStartedProps(input: {
  workflowId: string
  runId: string
  executionMode: WorkflowExecutionModeName
  surface: WorkflowRunSurface
}): WorkflowRunStartedProps {
  return {
    workflow_id: input.workflowId,
    run_id: input.runId,
    execution_mode: input.executionMode,
    surface: input.surface,
  }
}

export function workflowRunFinishedProps(
  workflowId: string,
  runId: string,
): WorkflowRunFinishedProps {
  return { workflow_id: workflowId, run_id: runId }
}

export function workflowPublishedProps(workflowId: string): WorkflowPublishedProps {
  return { workflow_id: workflowId }
}

export function workflowScheduledProps(
  workflowId: string,
  scheduleId: string,
): WorkflowScheduledProps {
  return { workflow_id: workflowId, schedule_id: scheduleId }
}
